// Почта для чека. НПД, ч. 5 ст. 14 422-ФЗ
//
// Живёт в ядре по той же причине, что номер и пароль: форма гасит кнопку по
// этим правилам, а сервер обязан проверить ровно то же ещё раз. Две
// реализации одного правила расходятся молча — и расходятся в сторону
// «клиент пустил, сервер отказал», то есть в тупик без объяснения.
//
// ПРОВЕРКА НАМЕРЕННО СЛАБАЯ. Единственная настоящая проверка адреса — это
// дошедшее письмо; всё, что здесь можно сделать, — отсечь опечатку вроде
// адреса без собаки. Строгий шаблон отвергает валидные адреса (плюс в имени,
// юникод в домене, длинная зона) — и отвергает их на экране, где человек уже
// заплатил и ждёт чек. Ошибиться в эту сторону дороже, чем принять кривой
// адрес: непринятый адрес виден сразу и злит, недоставленное письмо видно по
// возврату и чинится сменой адреса в профиле.

import { truncate } from './text.ts'

/**
 * Потолок длины — из RFC 5321: 254 знака на весь адрес. Не «на всякий
 * случай»: почтовые серверы такой адрес всё равно не примут, и принять его
 * в базу значит завести строку, на которую нельзя ничего отправить.
 */
export const EMAIL_MAX_LENGTH = 254

export type EmailProblem = 'empty' | 'malformed' | 'too_long'

/**
 * Каноническая форма: без пробелов по краям и в нижнем регистре.
 *
 * Регистр гасится ЦЕЛИКОМ, включая имя до собаки. По RFC имя
 * регистрозависимо, на практике так не ведёт себя ни один почтовый сервис, а
 * различать `Ivan@` и `ivan@` означало бы завести два адреса одного человека
 * и не понимать, на какой из них ушёл чек.
 */
export const normalizeEmail = (input: string): string => input.trim().toLowerCase()

/**
 * Управляющие знаки — вся категория Cc Юникода: U+0000–U+001F и
 * U+007F–U+009F. 11.09.2026
 *
 * Шаблон адреса их пропускал: `[^\s@]` отсекает пробельные знаки, а
 * управляющие в большинстве своём не пробельные. Нулевой байт так доходил до
 * базы, PostgreSQL не принимает его в text, и первый же запрос ручки падал:
 * все пять ручек почты в `auth/service.ts` отвечали 500 с текстом запроса и
 * самим адресом в теле ответа.
 *
 * Отклоняется диапазон целиком, хотя база падает только на нулевом байте.
 * Остальные знаки она принимает, и до правки запрос кода писал такие адреса в
 * `email_codes`. Но в адресе им взяться неоткуда: с клавиатуры они не
 * набираются и в поле не видны, так что это след вставки или битой
 * кодировки, а не опечатка, которую честнее пропустить. Слабой проверка от
 * этого быть не перестаёт — буквы любого алфавита, плюс и редкие знаки ASCII
 * проходят, как проходили.
 */
const hasControlChar = (text: string): boolean => {
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i)
    if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) return true
  }
  return false
}

/** Что не так с адресом. null — всё в порядке. */
export function checkEmail(input: string): EmailProblem | null {
  const email = normalizeEmail(input)
  if (email === '') return 'empty'
  if (email.length > EMAIL_MAX_LENGTH) return 'too_long'
  // После нормализации, а не до: перевод строки на краю скопированного адреса
  // срезается `trim` и в базу не попадает — отказывать за него не в чем.
  if (hasControlChar(email)) return 'malformed'
  // Одна собака, что-то до неё, домен с точкой после. Всё остальное —
  // забота почтового сервера, а не поля ввода.
  if (!/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(email)) return 'malformed'
  return null
}

/** Тексты отказов. Один источник: их печатают и сервер, и форма. */
export const EMAIL_PROBLEM_TEXT: Record<EmailProblem, string> = {
  empty: 'Без адреса чек отправить некуда',
  malformed: 'Похоже на опечатку — адрес выглядит как имя@почта.ру',
  too_long: `Адрес длиннее ${EMAIL_MAX_LENGTH} знаков`,
}

export const isValidEmail = (input: string): boolean => checkEmail(input) == null

/**
 * Для логов и служебных экранов: `ki***@gmail.com`. SMY-156
 *
 * Почта — категория «Идентификация» (docs/data-flow.md), и в логах ей делать
 * нечего целиком. Ровно тот же довод, что у `maskPhone`, и та же форма
 * ответа: начало, чтобы человек узнал свой адрес, и ничего, по чему его
 * узнал бы посторонний.
 *
 * ⚠️ ДОМЕН ОСТАЁТСЯ ВИДЕН, и это решение, а не недоделка. Он не опознаёт
 * человека — им пользуются миллионы, — зато отвечает на единственный вопрос,
 * ради которого в лог доставки вообще смотрят: у какого провайдера письма не
 * доходят. Скрыв домен, мы получили бы журнал, по которому нельзя разобрать
 * ни одной жалобы «код не пришёл».
 *
 * Два знака, а не один: односимвольные локальные части существуют, и `*@…`
 * не отличалось бы от `**@…`. Короткое имя маскируется целиком — дописывать
 * до двух знаков нечего, а показать `a` из адреса `a@…` значит не
 * замаскировать ничего.
 */
export function maskEmail(input: string): string {
  const email = normalizeEmail(input)
  const at = email.lastIndexOf('@')
  // Адрес без собаки сюда попасть не должен, но лог — не то место, где стоит
  // падать: маскируем целиком и отдаём, что есть.
  if (at <= 0) return '***'
  const [local, domain] = [email.slice(0, at), email.slice(at + 1)]
  const head = local.length > 2 ? truncate(local, 2) : ''
  return `${head}***@${domain}`
}
